/**
 * AppealReportContent
 *
 * Shared layout for the content/user report appeal screens.
 * Mirrors Flutter's ContentReportAppealScreen / UserReportAppealScreen
 * (lib/presentation/screens/content/content_report_appeal_screen.dart,
 *  lib/presentation/screens/user/user_report_appeal_screen.dart).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { InputText } from '@/presentation/components/common/InputText';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface AppealReportContentProps {
  title: string;
  subtitle: string;
  description: string;
  commentPlaceholder: string;
  emptyCommentError: string;
  submitLabel: string;
  comment: string;
  isSubmitting: boolean;
  hasCommentError: boolean;
  setComment: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const AppealReportContent: React.FC<AppealReportContentProps> = ({
  title,
  subtitle,
  description,
  commentPlaceholder,
  emptyCommentError,
  submitLabel,
  comment,
  isSubmitting,
  hasCommentError,
  setComment,
  onClose,
  onSubmit,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const buttonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  };

  const isSubmitDisabled = isSubmitting || comment.trim().length === 0;

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.base,
        }}
      >
        <div style={{ width: responsive.spacing['3xl'] }} />

        <span
          style={{
            flex: 1,
            textAlign: 'center',
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
          }}
        >
          {title}
        </span>

        <div
          style={{
            width: responsive.spacing['3xl'],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <button {...qaAttr({ id: 'appeal-report-close' })} onClick={onClose} style={buttonStyle}>
            <AssetIcon path="/assets/images/close.png" />
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: responsive.spacing.xl,
          paddingRight: responsive.spacing.xl,
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.lg,
        }}
      >
        <p
          style={{
            margin: 0,
            marginBottom: responsive.spacing.base,
            color: theme.text.primary,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
          }}
        >
          {subtitle}
        </p>

        <p
          style={{
            margin: 0,
            marginBottom: responsive.spacing['2xl'],
            color: theme.text.secondary,
            fontSize: responsive.font.base,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>

        <div style={{ marginBottom: responsive.spacing['2xl'] }}>
          <div
            style={{
              backgroundColor: theme.secondary,
              borderRadius: responsive.borderRadius.base,
              overflow: 'hidden',
            }}
          >
            <InputText
              qa={{ id: 'appeal-report-comment' }}
              value={comment}
              onChange={setComment}
              maxLength={InputLengthFormatters.reportMaxLength}
              placeholder={commentPlaceholder}
              backgroundColor="transparent"
              height={responsive.form.textareaHeight}
              padding={responsive.spacing.base}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          {hasCommentError && (
            <p
              style={{
                margin: 0,
                marginTop: responsive.spacing.xs,
                color: theme.danger,
                fontSize: responsive.font.sm,
              }}
            >
              {emptyCommentError}
            </p>
          )}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: responsive.spacing.lg,
          }}
        >
          <button
            {...qaAttr({ id: 'appeal-report-submit' })}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            style={{
              backgroundColor: theme.danger,
              color: theme.white,
              border: 'none',
              borderRadius: responsive.button.borderRadius.sm,
              padding: responsive.button.padding.xl,
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.semibold,
              cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
              opacity: comment.trim().length === 0 ? 0.5 : 1,
              fontFamily: 'inherit',
            }}
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {isSubmitting && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: theme.modalOverlay,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
